import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon, Input, Switch, Modal, Breadcrumb } from "../../ui";
import { DeleteConfirmationModal } from "../../ui/Modals";
import { DELIVERY_GROUP_TYPES, getTypeLabel } from "./utils";

import {
  useDeliveryGroupDetail,
  useToggleDeliveryGroupStatus,
  useDeleteDeliveryGroup,
  useUpdateDeliveryGroup,
  useDriverManagers,
  useDrivers,
  useDriverCompanies,
  useBusinessesLite,
} from "../../../hooks";

const DeliveryGroupDetailPage = () => {
  const { t } = useTranslation("delivery");
  const { id } = useParams();
  const groupId = parseInt(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const toggleGroupStatusMutation = useToggleDeliveryGroupStatus();
  const deleteGroupMutation = useDeleteDeliveryGroup();
  const updateGroupMutation = useUpdateDeliveryGroup();

  const {
    data: currentGroup,
    isLoading,
    error,
    isError,
    refetch: refetchGroup,
  } = useDeliveryGroupDetail(groupId);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    administrator_id: "",
    type: 0,
    enabled: true,
    drivers: [],
    driver_companies: [],
    business: [],
  });

  const [errors, setErrors] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [businessSearchInput, setBusinessSearchInput] = useState("");
  const [businessSearchTerm, setBusinessSearchTerm] = useState("");

  // Fetch data for selects
  const { data: driverManagersData } = useDriverManagers("all", "", 1);
  const { data: driversData } = useDrivers(searchTerm);
  const { data: driverCompaniesData } = useDriverCompanies();
  const { data: businessesData, isLoading: isLoadingBusinesses } =
    useBusinessesLite({
      enabled: isEditing,
      searchTerm: businessSearchTerm,
    });

  const driverManagers = driverManagersData?.users || [];
  const drivers = driversData?.drivers || [];
  const driverCompanies = driverCompaniesData?.driverCompanies || [];
  const businesses = businessesData || [];

  // Selected items state
  const [selectedDrivers, setSelectedDrivers] = useState(new Set());
  const [selectedDriverCompanies, setSelectedDriverCompanies] = useState(
    new Set(),
  );
  const [selectedBusinesses, setSelectedBusinesses] = useState(new Set());

  // Debounce search inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBusinessSearchTerm(businessSearchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [businessSearchInput]);

  // Load group data when it's available
  useEffect(() => {
    if (currentGroup && !isEditing) {
      // Handle administrator_id - it can be an object with id or just a number
      const adminId =
        currentGroup.administrator_id?.id ||
        currentGroup.administrator_id ||
        "";

      setFormData({
        name: currentGroup.name || "",
        administrator_id: adminId.toString(),
        type: currentGroup.type ?? DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS,
        enabled: currentGroup.enabled ?? true,
        drivers: [],
        driver_companies: [],
        business: [],
      });

      // Set selected drivers
      if (currentGroup.drivers) {
        if (Array.isArray(currentGroup.drivers)) {
          setSelectedDrivers(
            new Set(currentGroup.drivers.map((d) => d.id || d)),
          );
        } else if (typeof currentGroup.drivers === "string") {
          try {
            const driverIds = JSON.parse(currentGroup.drivers);
            setSelectedDrivers(
              new Set(Array.isArray(driverIds) ? driverIds : []),
            );
          } catch {
            setSelectedDrivers(new Set());
          }
        }
      }

      // Set selected driver companies
      if (
        currentGroup.driver_companies &&
        Array.isArray(currentGroup.driver_companies)
      ) {
        setSelectedDriverCompanies(
          new Set(currentGroup.driver_companies.map((c) => c.id || c)),
        );
      }

      // Set selected businesses
      if (currentGroup.business && Array.isArray(currentGroup.business)) {
        setSelectedBusinesses(
          new Set(currentGroup.business.map((b) => b.id || b)),
        );
      }
    }
  }, [currentGroup, isEditing]);

  const handleToggleGroupStatus = (checked) => {
    // If trying to deactivate (unchecking), show confirmation modal
    if (currentGroup?.enabled && !checked) {
      setShowDeactivateModal(true);
    } else if (!currentGroup?.enabled && checked) {
      // If activating (checking), do it directly
      handleConfirmToggle();
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !currentGroup.enabled;

      await toggleGroupStatusMutation.mutateAsync({
        groupId: currentGroup.id,
        enabled: newEnabledStatus,
      });

      await refetchGroup();

      setShowDeactivateModal(false);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGroupMutation.mutateAsync(currentGroup.id);
      navigate("/delivery/groups");
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleBackToDeliveryGroups = () => {
    navigate("/delivery/groups");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    setErrors({});
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {};

      // Compare name
      if (formData.name !== (currentGroup.name || "")) {
        payload.name = formData.name;
      }

      // Compare administrator_id (handle both object and number formats)
      const currentAdminId =
        currentGroup.administrator_id?.id || currentGroup.administrator_id;
      const formAdminId = formData.administrator_id
        ? parseInt(formData.administrator_id)
        : null;
      if (formAdminId !== currentAdminId) {
        payload.administrator_id = formAdminId;
      }

      // Compare type
      if (formData.type !== (currentGroup.type ?? 0)) {
        payload.type = formData.type;
      }

      // Compare enabled
      if (formData.enabled !== (currentGroup.enabled ?? true)) {
        payload.enabled = formData.enabled;
      }

      // Compare drivers array
      let currentDrivers = [];
      if (currentGroup.drivers) {
        if (Array.isArray(currentGroup.drivers)) {
          currentDrivers = currentGroup.drivers.map((d) => d.id || d).sort();
        } else if (typeof currentGroup.drivers === "string") {
          try {
            const parsed = JSON.parse(currentGroup.drivers);
            currentDrivers = Array.isArray(parsed) ? parsed.sort() : [];
          } catch {
            currentDrivers = [];
          }
        }
      }
      const newDrivers = Array.from(selectedDrivers).sort();
      if (JSON.stringify(currentDrivers) !== JSON.stringify(newDrivers)) {
        payload.drivers = JSON.stringify(newDrivers);
      }

      // Compare driver_companies array
      let currentDriverCompanies = [];
      if (
        currentGroup.driver_companies &&
        Array.isArray(currentGroup.driver_companies)
      ) {
        currentDriverCompanies = currentGroup.driver_companies
          .map((c) => c.id || c)
          .sort();
      }
      const newDriverCompanies = Array.from(selectedDriverCompanies).sort();
      if (
        JSON.stringify(currentDriverCompanies) !==
        JSON.stringify(newDriverCompanies)
      ) {
        payload.driver_companies = newDriverCompanies;
      }

      // Compare business array
      let currentBusinesses = [];
      if (currentGroup.business && Array.isArray(currentGroup.business)) {
        currentBusinesses = currentGroup.business.map((b) => b.id || b).sort();
      }
      const newBusinesses = Array.from(selectedBusinesses).sort();
      if (JSON.stringify(currentBusinesses) !== JSON.stringify(newBusinesses)) {
        payload.business = newBusinesses;
      }

      // If there are no changes, just close editing mode (like cancel)
      if (Object.keys(payload).length === 0) {
        setIsEditing(false);
        setErrors({});
        return;
      }

      // Only send the payload if there are actual changes
      await updateGroupMutation.mutateAsync({
        groupId: currentGroup.id,
        data: payload,
      });

      // Invalidate queries to update related components
      // Invalidate driverGroups to update DriverGroupTab
      await queryClient.invalidateQueries({ queryKey: ["driverGroups"] });

      // If administrator_id changed, also invalidate the affected driver managers
      if (payload.administrator_id !== undefined) {
        const oldAdminId =
          currentGroup.administrator_id?.id || currentGroup.administrator_id;
        const newAdminId = payload.administrator_id;

        // Invalidate old administrator's data
        if (oldAdminId) {
          await queryClient.invalidateQueries({
            queryKey: ["driverManager", oldAdminId],
          });
        }

        // Invalidate new administrator's data
        if (newAdminId) {
          await queryClient.invalidateQueries({
            queryKey: ["driverManager", newAdminId],
          });
        }
      }

      setIsEditing(false);
      setErrors({});
      await refetchGroup();
    } catch (error) {
      console.error("Update delivery group error:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    // Reset form data to current group values
    if (currentGroup) {
      // Handle administrator_id - it can be an object with id or just a number
      const adminId =
        currentGroup.administrator_id?.id ||
        currentGroup.administrator_id ||
        "";

      setFormData({
        name: currentGroup.name || "",
        administrator_id: adminId.toString(),
        type: currentGroup.type ?? DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS,
        enabled: currentGroup.enabled ?? true,
        drivers: [],
        driver_companies: [],
        business: [],
      });

      if (currentGroup.drivers) {
        if (Array.isArray(currentGroup.drivers)) {
          setSelectedDrivers(
            new Set(currentGroup.drivers.map((d) => d.id || d)),
          );
        }
      }

      if (
        currentGroup.driver_companies &&
        Array.isArray(currentGroup.driver_companies)
      ) {
        setSelectedDriverCompanies(
          new Set(currentGroup.driver_companies.map((c) => c.id || c)),
        );
      }

      if (currentGroup.business && Array.isArray(currentGroup.business)) {
        setSelectedBusinesses(
          new Set(currentGroup.business.map((b) => b.id || b)),
        );
      }
    }
  };

  const createToggleFunction = (setter) => (id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleDriver = createToggleFunction(setSelectedDrivers);
  const toggleDriverCompany = createToggleFunction(setSelectedDriverCompanies);
  const toggleBusiness = createToggleFunction(setSelectedBusinesses);

  if (isLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500 sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-sm text-gray-400 sm:text-base">
            {t("groupDetail.states.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !currentGroup)) {
    return (
      <div className="px-4 py-8 text-center sm:py-12">
        <Icon
          name="Users"
          className="mx-auto mb-3 h-8 w-8 text-gray-400 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="mt-2 text-sm font-medium text-gray-300 sm:text-base">
          {t("groupDetail.states.notFoundTitle")}
        </h3>
        <p className="mt-1 text-xs text-gray-400 sm:text-sm">
          {error?.message || t("groupDetail.states.notFoundDescription")}
        </p>
        <button
          onClick={handleBackToDeliveryGroups}
          className="mt-3 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white transition-colors hover:bg-blue-700 sm:mt-4 sm:px-4 sm:text-base"
        >
          {t("groupDetail.actions.backToGroups")}
        </button>
      </div>
    );
  }

  const groupName = currentGroup?.name || t("groupDetail.fallbacks.unknown");

  const breadcrumbItems = [
    {
      label: t("groupDetail.breadcrumb.delivery"),
      path: "/delivery/groups",
      icon: "Truck",
    },
    {
      label: groupName,
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Group Profile Header */}
      <div className="rounded-lg border border-gray-800 bg-gray-900">
        <div className="border-b border-gray-800 p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500 to-purple-500 sm:h-16 sm:w-16">
                <Icon
                  name="Users"
                  className="h-6 w-6 text-white sm:h-8 sm:w-8"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-lg font-bold text-white sm:text-2xl">
                  {groupName}
                </h1>
                <div className="mt-1 flex items-center space-x-2">
                  <Icon name="Tag" className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-400">
                    {t(`groupTypes.${currentGroup?.type}`, {
                      defaultValue: getTypeLabel(currentGroup?.type),
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex cursor-pointer items-center justify-center rounded-lg bg-blue-600 p-2 text-xs text-white transition-colors hover:bg-blue-700 sm:space-x-2 sm:px-4 sm:py-2 sm:text-sm"
                    title={t("common:actions.edit")}
                  >
                    <Icon name="Edit" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t("common:actions.edit")}
                    </span>
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    disabled={deleteGroupMutation.isPending}
                    className="flex cursor-pointer items-center justify-center rounded-lg bg-red-600 p-2 text-xs text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:space-x-2 sm:px-4 sm:py-2 sm:text-sm"
                    title={t("common:actions.delete")}
                  >
                    <Icon name="Trash2" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t("common:actions.delete")}
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={updateGroupMutation.isPending}
                    className="flex cursor-pointer items-center justify-center rounded-lg bg-green-600 p-2 text-xs text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 sm:space-x-2 sm:px-4 sm:py-2 sm:text-sm"
                    title={t("common:actions.save")}
                  >
                    <Icon name="Check" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {updateGroupMutation.isPending
                        ? t("groupDetail.actions.saving")
                        : t("common:actions.save")}
                    </span>
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={updateGroupMutation.isPending}
                    className="flex cursor-pointer items-center justify-center rounded-lg bg-gray-600 p-2 text-xs text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:space-x-2 sm:px-4 sm:py-2 sm:text-sm"
                    title={t("common:actions.cancel")}
                  >
                    <Icon name="X" className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {t("common:actions.cancel")}
                    </span>
                  </button>
                </>
              )}
              <div
                className="flex items-center space-x-2"
                onClick={(e) => e.stopPropagation()}
                title={
                  currentGroup.enabled
                    ? t("groupDetail.actions.deactivate")
                    : t("groupDetail.actions.activate")
                }
              >
                <span
                  className={`text-md font-medium ${
                    currentGroup.enabled ? "text-green-400" : "text-red-400"
                  }`}
                >
                  {currentGroup.enabled
                    ? t("common:states.enabled")
                    : t("common:states.disabled")}
                </span>
                <Switch
                  checked={!!currentGroup.enabled}
                  onCheckedChange={handleToggleGroupStatus}
                  disabled={toggleGroupStatusMutation.isPending}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-4 p-4 sm:space-y-6 sm:p-6">
          {/* Basic Information */}
          <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 sm:p-6">
            <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
              <Icon name="Info" className="mr-2 h-5 w-5" />
              {t("groupDetail.sections.basicInformation")}
            </h3>

            {isEditing ? (
              <div className="space-y-4">
                <Input
                  label={t("groupDetail.fields.name.label")}
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  error={errors.name}
                  placeholder={t("groupDetail.fields.name.placeholder")}
                  required
                />

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">
                    {t("groupDetail.fields.deliveryManager.label")}{" "}
                    <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.administrator_id}
                    onChange={(e) =>
                      handleInputChange("administrator_id", e.target.value)
                    }
                    className={`w-full rounded-lg border bg-gray-700 px-3 py-2 text-white ${
                      errors.administrator_id
                        ? "border-red-500"
                        : "border-gray-600"
                    } custom-select focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                  >
                    <option value="">
                      {t(
                        "groupDetail.fields.deliveryManager.selectPlaceholder",
                      )}
                    </option>
                    {driverManagers.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.name} {manager.lastname}{" "}
                        {manager.email ? `(${manager.email})` : ""}
                      </option>
                    ))}
                  </select>
                  {errors.administrator_id && (
                    <p className="mt-1 text-xs text-red-400">
                      {errors.administrator_id}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-300">
                    {t("groupDetail.fields.type.label")}{" "}
                    <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      handleInputChange("type", parseInt(e.target.value))
                    }
                    className="custom-select w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value={DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS}>
                      {t("groupTypes.inHouseDrivers")}
                    </option>
                    <option value={DELIVERY_GROUP_TYPES.DELIVERY_COMPANIES}>
                      {t("groupTypes.deliveryCompanies")}
                    </option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">
                    {t("groupDetail.summary.name")}
                  </span>
                  <span className="text-white">
                    {currentGroup?.name ||
                      t("groupDetail.fallbacks.notAvailable")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">
                    {t("groupDetail.summary.type")}
                  </span>
                  <span className="text-white">
                    {t(`groupTypes.${currentGroup?.type}`, {
                      defaultValue: getTypeLabel(currentGroup?.type),
                    })}
                  </span>
                </div>
                {currentGroup?.administrators &&
                  currentGroup.administrators.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">
                        {t("groupDetail.summary.deliveryManager")}
                      </span>
                      <span className="text-white">
                        {currentGroup.administrators[0].name}{" "}
                        {currentGroup.administrators[0].lastname}
                      </span>
                    </div>
                  )}
                {currentGroup?.created_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      {t("groupDetail.summary.created")}
                    </span>
                    <span className="text-white">
                      {new Date(currentGroup.created_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Drivers Selection (when type = 0) */}
          {formData.type === DELIVERY_GROUP_TYPES.IN_HOUSE_DRIVERS && (
            <div className="rounded-lg border border-gray-700 bg-gray-800 p-4 sm:p-6">
              <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
                <Icon name="Users" className="mr-2 h-5 w-5" />
                {t("groupDetail.sections.drivers")}
              </h3>

              {isEditing ? (
                <div className="space-y-4">
                  <Input
                    label={t("groupDetail.fields.searchDrivers.label")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t(
                      "groupDetail.fields.searchDrivers.placeholder",
                    )}
                  />

                  <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-600 bg-gray-700 p-4">
                    {drivers.length === 0 ? (
                      <p className="py-4 text-center text-sm text-gray-400">
                        {t("groupDetail.states.noDriversFound")}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {drivers.map((driver) => {
                          const name =
                            driver.name && driver.lastname
                              ? `${driver.name} ${driver.lastname}`
                              : driver.name ||
                                t("groupDetail.fallbacks.unknown");
                          const isSelected = selectedDrivers.has(driver.id);

                          return (
                            <div
                              key={driver.id}
                              className={`flex items-center justify-between rounded-lg border p-3 ${
                                isSelected
                                  ? "border-blue-500 bg-blue-500/10"
                                  : "border-gray-500 bg-gray-600/50"
                              }`}
                            >
                              <div className="flex flex-1 items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleDriver(driver.id)}
                                  className="h-4 w-4 rounded border-gray-500 bg-gray-600 text-blue-600 focus:ring-blue-500"
                                />
                                {driver.photo ? (
                                  <img
                                    src={driver.photo}
                                    alt={name}
                                    className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextSibling.style.display =
                                        "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-600 ${
                                    driver.photo ? "hidden" : ""
                                  }`}
                                >
                                  <Icon
                                    name="User"
                                    className="h-4 w-4 text-gray-400"
                                  />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white">
                                    {name}
                                  </p>
                                  {driver.email && (
                                    <p className="text-xs text-gray-400">
                                      {driver.email}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {errors.drivers && (
                    <p className="text-xs text-red-400">{errors.drivers}</p>
                  )}
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-600 bg-gray-700 p-4">
                  {currentGroup?.drivers &&
                  Array.isArray(currentGroup.drivers) &&
                  currentGroup.drivers.length > 0 ? (
                    <div className="space-y-2">
                      {currentGroup.drivers.map((driver) => {
                        const name =
                          driver.name && driver.lastname
                            ? `${driver.name} ${driver.lastname}`
                            : driver.name || t("groupDetail.fallbacks.unknown");
                        return (
                          <div
                            key={driver.id || driver}
                            className="flex items-center space-x-3 rounded-lg border border-gray-500 bg-gray-600 p-3"
                          >
                            {driver.photo ? (
                              <img
                                src={driver.photo}
                                alt={name}
                                className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-500 ${
                                driver.photo ? "hidden" : ""
                              }`}
                            >
                              <Icon
                                name="User"
                                className="h-5 w-5 text-gray-400"
                              />
                            </div>
                            <div>
                              <p className="text-sm text-white">{name}</p>
                              {driver.email && (
                                <p className="mt-1 text-xs text-gray-400">
                                  {driver.email}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="py-4 text-center text-sm text-gray-400">
                      {t("groupDetail.states.noDriversAssigned")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Driver Companies Selection (when type = 1) */}
          {formData.type === DELIVERY_GROUP_TYPES.DELIVERY_COMPANIES && (
            <div className="space-y-4 rounded-lg border border-gray-700 bg-gray-800 p-4 sm:p-6">
              <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
                <Icon name="Truck" className="mr-2 h-5 w-5" />
                {t("groupDetail.sections.deliveryCompanies")}
              </h3>

              {isEditing ? (
                <div className="space-y-4">
                  <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-600 bg-gray-700 p-4">
                    {driverCompanies.length === 0 ? (
                      <p className="py-4 text-center text-sm text-gray-400">
                        {t("groupDetail.states.noDeliveryCompaniesFound")}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {driverCompanies.map((company) => {
                          const isSelected = selectedDriverCompanies.has(
                            company.id,
                          );

                          return (
                            <div
                              key={company.id}
                              className={`flex items-center justify-between rounded-lg border p-3 ${
                                isSelected
                                  ? "border-blue-500 bg-blue-500/10"
                                  : "border-gray-500 bg-gray-600/50"
                              }`}
                            >
                              <div className="flex flex-1 items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    toggleDriverCompany(company.id)
                                  }
                                  className="h-4 w-4 rounded border-gray-500 bg-gray-600 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                  <p className="text-sm font-medium text-white">
                                    {company.name ||
                                      t("groupDetail.fallbacks.companyWithId", {
                                        id: company.id,
                                      })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {errors.driver_companies && (
                    <p className="text-xs text-red-400">
                      {errors.driver_companies}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {currentGroup?.driver_companies &&
                  Array.isArray(currentGroup.driver_companies) &&
                  currentGroup.driver_companies.length > 0 ? (
                    currentGroup.driver_companies.map((company) => (
                      <div
                        key={company.id || company}
                        className="rounded-lg border border-gray-600 bg-gray-700 p-3"
                      >
                        <p className="text-sm text-white">
                          {company.name ||
                            t("groupDetail.fallbacks.companyWithId", {
                              id: company.id || company,
                            })}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">
                      {t("groupDetail.states.noDeliveryCompaniesAssigned")}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Businesses Selection */}
          <div className="space-y-4 rounded-lg border border-gray-700 bg-gray-800 p-4 sm:p-6">
            <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
              <Icon name="Store" className="mr-2 h-5 w-5" />
              {t("groupDetail.sections.businesses")}
            </h3>

            {isEditing ? (
              <div className="space-y-4">
                <Input
                  label={t("groupDetail.fields.searchBusinesses.label")}
                  value={businessSearchInput}
                  onChange={(e) => setBusinessSearchInput(e.target.value)}
                  placeholder={t(
                    "groupDetail.fields.searchBusinesses.placeholder",
                  )}
                />

                <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-600 bg-gray-700 p-4">
                  {isLoadingBusinesses ? (
                    <div className="py-4 text-center">
                      <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
                      <p className="mt-2 text-sm text-gray-400">
                        {t("groupDetail.states.loadingBusinesses")}
                      </p>
                    </div>
                  ) : businesses.length === 0 ? (
                    <p className="py-4 text-center text-sm text-gray-400">
                      {t("groupDetail.states.noBusinessesFound")}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {businesses.map((business) => {
                        const isSelected = selectedBusinesses.has(business.id);

                        return (
                          <div
                            key={business.id}
                            className={`flex items-center justify-between rounded-lg border p-3 ${
                              isSelected
                                ? "border-blue-500 bg-blue-500/10"
                                : "border-gray-500 bg-gray-600/50"
                            }`}
                          >
                            <div className="flex flex-1 items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleBusiness(business.id)}
                                className="h-4 w-4 rounded border-gray-500 bg-gray-600 text-blue-600 focus:ring-blue-500"
                              />
                              {business.logo ? (
                                <img
                                  src={business.logo}
                                  alt={business.name}
                                  className="h-8 w-8 rounded object-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-600">
                                  <Icon
                                    name="Store"
                                    className="h-4 w-4 text-gray-400"
                                  />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {business.name}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {errors.business && (
                  <p className="text-xs text-red-400">{errors.business}</p>
                )}
              </div>
            ) : (
              <div className="max-h-70 overflow-y-auto rounded-lg border border-gray-600 bg-gray-700 p-4">
                {currentGroup?.business &&
                Array.isArray(currentGroup.business) &&
                currentGroup.business.length > 0 ? (
                  <div className="space-y-2">
                    {currentGroup.business.map((business) => (
                      <div
                        key={business.id || business}
                        className="flex items-center space-x-3 rounded-lg border border-gray-500 bg-gray-600 p-3"
                      >
                        {business.logo ? (
                          <img
                            src={business.logo}
                            alt={business.name}
                            className="h-10 w-10 flex-shrink-0 rounded object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-gray-500">
                            <Icon
                              name="Store"
                              className="h-5 w-5 text-gray-400"
                            />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-white">
                            {business.name ||
                              t("groupDetail.fallbacks.businessWithId", {
                                id: business.id || business,
                              })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-4 text-center text-sm text-gray-400">
                    {t("groupDetail.states.noBusinessesAssigned")}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deactivate Group Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title={t("groupDetail.deactivateModal.title")}
        subtitle={t("groupDetail.deactivateModal.subtitle")}
        maxWidth="max-w-md"
      >
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/20 sm:h-10 sm:w-10">
                <Icon
                  name="AlertTriangle"
                  className="h-4 w-4 text-red-400 sm:h-5 sm:w-5"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium text-white">
                {t("groupDetail.deactivateModal.confirmTitle", {
                  name: groupName,
                })}
              </h3>
              <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                {t("groupDetail.deactivateModal.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <button
              onClick={handleConfirmToggle}
              disabled={toggleGroupStatusMutation.isPending}
              className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
            >
              {toggleGroupStatusMutation.isPending ? (
                <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-white sm:h-4 sm:w-4"></div>
              ) : null}
              <span>
                {toggleGroupStatusMutation.isPending
                  ? t("groupDetail.deactivateModal.deactivating")
                  : t("groupDetail.actions.deactivate")}
              </span>
            </button>
            <button
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleGroupStatusMutation.isPending}
              className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
            >
              {t("common:actions.cancel")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t("groupDetail.deleteModal.title")}
        itemName={groupName}
        itemType={t("groupDetail.deleteModal.itemType")}
        isLoading={deleteGroupMutation.isPending}
      />
    </div>
  );
};

export default DeliveryGroupDetailPage;
